import type { TimelineEvent } from "@productlane/api/src/browser"
import type {
	AiShadowReplyChannel,
	AiShadowReplyStatus,
	ThreadActivityPayloadMap,
} from "@productlane/lib/src/browser"
import { timelineEventTimestamp } from "@productlane/api/src/browser"
import { parseThreadActivity } from "@productlane/lib/src/browser"

export const SHADOW_REPLY_EVENT_TYPE = "ai_shadow_reply"

export type ShadowReplyChannel = AiShadowReplyChannel
export type ShadowReplyStatus = AiShadowReplyStatus
export type ShadowReplyPayload = ThreadActivityPayloadMap["ai_shadow_reply"]

export function parseShadowReplyPayload(
	payload: unknown,
): ShadowReplyPayload | null {
	try {
		const parsed = parseThreadActivity(SHADOW_REPLY_EVENT_TYPE, payload)
		return parsed.eventType === SHADOW_REPLY_EVENT_TYPE ? parsed.payload : null
	} catch {
		return null
	}
}

export function isShadowReplyEvent(event: TimelineEvent): boolean {
	return (
		event.type === "activity" &&
		event.threadActivity.eventType === SHADOW_REPLY_EVENT_TYPE
	)
}

// Canned acknowledgements and close-loop mail go out as AUTO_REPLY and answer nothing.
const isAnswerToCustomer = (event: TimelineEvent): boolean => {
	switch (event.type) {
		case "email":
			return (
				event.email.type === "OUTBOUND" && event.email.source !== "AUTO_REPLY"
			)
		case "slackMessage":
			return event.slackMessage.type === "OUTBOUND"
		case "chatMessage":
			return event.chatMessage.type === "OUTBOUND"
		case "live-chat-message":
			return (
				event.liveChatMessage.role === "AGENT" ||
				event.liveChatMessage.role === "AI"
			)
		default:
			return false
	}
}

/** The newest answer to the customer in `events`, for `isHiddenShadowReply`. */
export function latestAnswerAt(events: TimelineEvent[]): number | null {
	let latest: number | null = null
	for (const event of events) {
		if (!isAnswerToCustomer(event)) continue
		const at = timelineEventTimestamp(event)
		if (latest === null || at > latest) latest = at
	}
	return latest
}

// A superseded or unreadable preview stays out of the timeline entirely, and so
// does a pending one someone answered after: its Send would answer twice.
export function isHiddenShadowReply(
	event: TimelineEvent,
	answeredAt: number | null = null,
): boolean {
	if (event.type !== "activity" || !isShadowReplyEvent(event)) return false
	const payload = parseShadowReplyPayload(event.threadActivity.payload)
	if (!payload || payload.status === "superseded") return true
	return (
		payload.status === "pending" &&
		answeredAt !== null &&
		answeredAt > event.threadActivity.createdAt
	)
}

const CHANNEL_LABELS: Record<ShadowReplyChannel, string> = {
	email: "email",
	slack: "Slack",
	teams: "Microsoft Teams",
	support_portal: "the support portal",
	api: "the API",
}

export function shadowReplyChannelLabel(channel: ShadowReplyChannel): string {
	return CHANNEL_LABELS[channel]
}

export type ShadowReplyAction =
	| "send"
	| "edit"
	| "regenerate"
	| "decline"
	| "activate"
	| "stop"

// A close-loop preview is the workspace's template: no agent rewrite, and its
// switch is auto-send in the close loop settings, not a channel.
export function shadowReplyActions({
	status,
	role,
	isCloseLoop = false,
}: {
	status: ShadowReplyStatus
	role: string | null | undefined
	isCloseLoop?: boolean
}): ShadowReplyAction[] {
	if (status !== "pending" || !role || role === "VIEWER") return []
	const actions: ShadowReplyAction[] = isCloseLoop
		? ["send", "decline"]
		: ["send", "edit", "regenerate", "decline", "activate"]
	if (role === "ADMIN") actions.push("stop")
	return actions
}

export type ShadowReplyLesson = NonNullable<ShadowReplyPayload["lesson"]>

export interface ShadowReplyStatusLine {
	label: string
	/** Opens the popover with the note and what the agent took from it; null when there is nothing more to show. */
	trigger: string | null
	note: string | null
	lesson: ShadowReplyLesson | null
}

// Kept to one short line: the note and the rule, which run long, live in the popover.
export function shadowReplyStatusLine({
	status,
	originalHtml,
	note,
	lesson,
}: {
	status: ShadowReplyStatus
	originalHtml?: string | null
	note?: string | null
	lesson?: ShadowReplyLesson | null
}): ShadowReplyStatusLine | null {
	const shown =
		lesson?.target === "docs" ||
		(lesson?.target === "instructions" && !!lesson.rule)
			? lesson
			: null
	const lessonTrigger = !shown
		? null
		: shown.target === "docs"
			? "Help center update"
			: shown.undone
				? "Rule removed"
				: "Learned 1 rule"
	if (status === "sent") {
		if (!originalHtml) {
			return {
				label: "Sent to the customer",
				trigger: null,
				note: null,
				lesson: null,
			}
		}
		return {
			label: "Sent with your edits",
			trigger: lessonTrigger,
			note: null,
			lesson: shown,
		}
	}
	if (status === "revising" || status === "declined") {
		return {
			label: status === "revising" ? "Rewriting with your note" : "Declined",
			trigger: note ? "View note" : lessonTrigger,
			note: note || null,
			lesson: shown,
		}
	}
	return null
}
